import config from 'Cloud/config';
import React, { ReactElement, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { renderBizPaidEditorDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers';
import { SharingNewDropdown } from 'reactApp/components/SharingNew/Dropdown/SharingNewDropdown';
import {
    ACCESS_FILE_RIGHTS_OPTIONS,
    ACCESS_FOLDER_RIGHTS_OPTIONS,
    QA_VALUE,
    TOOLTIP_OFFSET,
} from 'reactApp/components/SharingNew/SharingNew.constants';
import { sendGa } from 'reactApp/components/SharingNew/SharingNew.helpers';
import { AccessStatus } from 'reactApp/components/SharingNew/SharingNew.types';
import styles from 'reactApp/components/SharingNew/Weblink/SharingNewWeblink.css';
import { getEditorsFor, getIsFileForCoEditing } from 'reactApp/modules/editor/editor.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { toggleWeblinkEditableRequest, toggleWeblinkUploadRequest } from 'reactApp/modules/modifying/modifying.actions';
import { getItemById } from 'reactApp/modules/storage/storage.selectors';
import { RootState } from 'reactApp/store';
import { AccessRights } from 'reactApp/types/Tree';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { noop } from 'reactApp/utils/helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

const IS_FREE_B2B_BIZ_EDITORS_USER = config.get('FREE_B2B_BIZ_EDITORS_USER');

const getAccessRights = (isReadOnly, isFolder, isPublicReadWrite): AccessStatus => {
    return (isPublicReadWrite && !isReadOnly && (isFolder ? AccessStatus.Upload : AccessStatus.Edit)) || AccessStatus.ReadOnly;
};

export const useAccessRights = (item, isSharedOrMouted) => {
    const dispatch = useDispatch();
    const isPhone = EnvironmentSelectors.isPhone();
    const isFolder = item?.isFolder;
    const isFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, item));
    // в онпремисе не даём паблишить файлы на редактирование
    const isDocumentForEdit = !IS_ONPREMISE && (item?.kind === 'document' && IS_FREE_B2B_BIZ_EDITORS_USER ? true : isFileForCoEditing);
    const isPublic = Boolean(item?.weblink);
    const parent = useSelector((state: RootState) => getItemById(state, item?.parent));
    const isReadOnlyItem = item?.isReadOnly;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isReadOnly = !isReadOnlyItem && isSharedOrMouted.isMounted ? parent?.isReadOnly : isReadOnlyItem;

    const options = isFolder ? ACCESS_FOLDER_RIGHTS_OPTIONS : ACCESS_FILE_RIGHTS_OPTIONS;
    const isRO = isFolder ? isReadOnly : isReadOnly || !isDocumentForEdit;
    const isPublicReadWrite = item?.weblinkAccessRights === AccessRights.rw;
    const accessRights = getAccessRights(isReadOnly, isFolder, isPublicReadWrite);

    const isEditable = useSelector((state: RootState) => getEditorsFor(state, item))?.length;

    const renderAccessRightsDropdown = useCallback(
        (onClick): ReactElement => {
            const list = Object.values(options).map((item) => ({
                id: item.id,
                text: item.text,
                check: item.id === accessRights,
                qaValue: item.id,
            }));

            return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
        },
        [accessRights, options]
    );

    const handleAccessRightsChange = useCallback(
        (id): void => {
            if (!isPublic || accessRights === id) {
                return;
            }

            sendGa('access-rights');
            sendPaymentGa({
                eventCategory: ECategoryGa.public,
                action: 'edit-type-access',
                type_access: id,
                public_id: item?.weblink,
            });

            if (isDocumentForEdit) {
                if (IS_FREE_B2B_BIZ_EDITORS_USER && !isEditable && isReadOnly) {
                    renderBizPaidEditorDialog();
                    return;
                }

                dispatch(toggleWeblinkEditableRequest({ item }));
            } else {
                dispatch(toggleWeblinkUploadRequest({ item }));
            }
        },
        [accessRights, dispatch, isDocumentForEdit, isEditable, isPublic, isReadOnly, item]
    );

    const renderAccessRights = useCallback((): ReactElement => {
        const item = options[accessRights];

        return (
            <div className={styles.rightsItem} data-qa-item={QA_VALUE.accessItem}>
                <span className={styles.rightsTitle}>Пользователи могут</span>
                <div className={styles.dropdown}>
                    <Dropdown
                        withinDialog
                        theme="octavius"
                        qaValue={item?.id}
                        as={SharingNewDropdown}
                        value={isPhone ? item.text : item.text.toLowerCase()}
                        content={renderAccessRightsDropdown}
                        onClickItem={handleAccessRightsChange}
                        placement={tooltipPlacements.BOTTOM_LEFT}
                        qaValueContent={QA_VALUE.accessDropdownContent}
                        tooltipOffset={TOOLTIP_OFFSET}
                        animatedTooltip
                        description="Пользователи могут"
                        tooltipClassName={isPhone ? styles.tooltip : undefined}
                        isPhone={isPhone}
                    />
                </div>
            </div>
        );
    }, [accessRights, handleAccessRightsChange, isPhone, options, renderAccessRightsDropdown]);

    return isRO ? noop : renderAccessRights;
};
